import { usePartyCompanyNumberAvailabilityQuery } from "Api/Queries/Party/usePartyCompanyNumberAvailabilityQuery";
import { useAres } from "Hooks/useAres";
import type { PartyDto, Region } from "Infrastructure/Api/Api";
import { useState } from "react";

type ReturnValue = {
  isLoading: boolean;
  isAvailable: boolean;
  aresData: PartyDto | null;
  isInvalid: boolean;
  /** Clears the last register result — call when the market/region changes so stale data doesn't linger. */
  reset: () => void;
};

export const useAvailableAresData = (
  companyNumber: string,
  region: Region | null | undefined,
  accessRoleCode?: string,
): ReturnValue => {
  const [data, setData] = useState<PartyDto | null>(null);

  const {
    isFetchingAres,
    isError: isAresError,
    isSuccess: isAresSuccess,
  } = useAres({
    identifierNumber: companyNumber,
    region,
    onAresData: (data) => {
      setData(data);
    },
    onError: () => {
      setData(null);
    },
  });

  // isFetching, not isLoading: after going back to this form the previous "available" answer is
  // served from cache and would enable submit before the re-check returns - that is how one
  // company got registered twice.
  const { isFetching: isAvailabilityLoading, isSuccess: isAvailable } =
    usePartyCompanyNumberAvailabilityQuery(companyNumber, {
      enabled: companyNumber.length === 8 && isAresSuccess,
      accessRoleCode,
      // Scope the taken-check to the selected market — the same 8-digit number can legitimately
      // exist in both CZ and SK registers for two different companies.
      region,
    });

  return {
    isAvailable,
    aresData: data,
    isInvalid: isAresError,
    isLoading: isFetchingAres || isAvailabilityLoading,
    reset: () => setData(null),
  };
};
